import { useCallback, useState } from "react";

interface DeleteImpactSuite {
  name: string;
}

interface DeleteImpactResponse {
  affectedTestSuites?: DeleteImpactSuite[];
}

interface DeleteImpactParams {
  projectId?: string;
  moduleId?: string;
  requirementId?: string;
  testcaseId?: string;
}

interface UseDeleteImpactModalOptions {
  accountId?: string;
  fetchDeleteImpact: (
    accountId: string,
    params: DeleteImpactParams,
  ) => Promise<DeleteImpactResponse>;
  onClose?: () => void;
}

const useDeleteImpactModal = ({
  accountId,
  fetchDeleteImpact,
  onClose,
}: UseDeleteImpactModalOptions) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [affectedSuites, setAffectedSuites] = useState<string[]>([]);
  const [isImpactLoading, setImpactLoading] = useState<boolean>(false);

  const closeDeleteModal = useCallback(() => {
    setModalOpen(false);
    setAffectedSuites([]);
    setImpactLoading(false);
    onClose?.();
  }, [onClose]);

  const openDeleteModal = useCallback(
    async (params: DeleteImpactParams) => {
      setModalOpen(true);

      if (!accountId) {
        setImpactLoading(false);
        setAffectedSuites([]);
        return;
      }

      setImpactLoading(true);
      try {
        const impact = await fetchDeleteImpact(accountId, params);
        setAffectedSuites(
          (impact?.affectedTestSuites || []).map((suite) => suite.name),
        );
      } catch (error) {
        setAffectedSuites([]);
      } finally {
        setImpactLoading(false);
      }
    },
    [accountId, fetchDeleteImpact],
  );

  return {
    isModalOpen,
    affectedSuites,
    isImpactLoading,
    openDeleteModal,
    closeDeleteModal,
  };
};

export default useDeleteImpactModal;
